import { UseQueryResult, UseSuspenseQueryResult } from "@tanstack/react-query";
import client, { moreMemoryApiClient } from "../client";
import type {
  Article,
  ArticleReachStatistics,
  ExtendedArticle,
  NotificationMethod,
  Notification,
  RecipientGroup,
  ArticleSubmitType,
  ArticleNotificationReach,
  ArticleReadStatistics,
  ExportImageData,
} from "@/types";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

export const createArticle = async (article: ArticleSubmitType) => {
  const response = await client.post("/createArticle", {
    article,
  });
  return response.data;
};

export const getArticleNotificationReach = async (
  companyIds: string[],
  groups: RecipientGroup[],
  operational: boolean,
) => {
  const response = await client.post(`/getArticleNotificationReachStatistics`, {
    companyIds,
    groups,
    operational,
  });
  return response.data as ArticleNotificationReach;
};

export const createArticleNotification = async (
  articleId: string,
  groups: RecipientGroup[],
  companiesIds: string[],
  notificationMethods: NotificationMethod[],
) => {
  return await moreMemoryApiClient.post(`/createArticleNotification`, {
    articleId,
    companiesIds,
    groups,
    notificationMethods,
  });
};

export const articleNotificationsOptions = (articleId: string) => ({
  queryKey: ["article-notifications", articleId],
  queryFn: async () => {
    const response = await client.get(
      `/getArticleNotifications?articleId=${articleId}`,
    );
    return response.data;
  },
});

export const useArticleNotifications = (
  articleId: string = "",
): UseQueryResult<Notification[]> => {
  return useBuildingQuery({
    ...articleNotificationsOptions(articleId),
    enabled: !!articleId,
  });
};

export const allArticlesOptions = {
  queryKey: ["all-articles"],
  queryFn: async () => {
    const response = await client.get("/getAllArticles");
    return response.data;
  },
};

export const useAllArticles = (): UseSuspenseQueryResult<Article[]> => {
  return useBuildingSuspenseQuery(allArticlesOptions);
};

// For all users
export const articleOptions = (articleId: string) => ({
  queryKey: ["article", articleId],
  queryFn: async () => {
    const response = await client.get(`/getArticle?articleId=${articleId}`);
    return response.data;
  },
});

export const useArticle = (
  articleId: string,
): UseSuspenseQueryResult<ExtendedArticle> => {
  return useBuildingSuspenseQuery(articleOptions(articleId));
};

// For building admins only
export const fullArticleOptions = (articleId: string) => ({
  queryKey: ["full-article", articleId],
  queryFn: async () => {
    const response = await client.get(`/getFullArticle?articleId=${articleId}`);
    return response.data;
  },
});

export const useFullArticle = (
  articleId: string,
): UseSuspenseQueryResult<ExtendedArticle> => {
  return useBuildingSuspenseQuery(fullArticleOptions(articleId));
};

export const availableArticlesOptions = (userId: string) => ({
  queryKey: ["available-articles", userId],
  queryFn: async () => {
    const response = await client.get(`/getAvailableArticles?userId=${userId}`);
    return response.data;
  },
});

export const useAvailableArticles = (
  userId: string,
): UseSuspenseQueryResult<Article[]> => {
  return useBuildingSuspenseQuery(availableArticlesOptions(userId));
};

export const articleReachOptions = (articleId: string) => ({
  queryKey: ["article-reach-statistics", articleId],
  queryFn: async () => {
    const response = await client.get(
      `/getArticleReachStatistics?articleId=${articleId}`,
    );
    return response.data;
  },
});

export const useArticleReachStatistics = (
  articleId: string,
): UseQueryResult<ArticleReachStatistics> => {
  return useBuildingQuery(articleReachOptions(articleId));
};

export const useArticleReadStatistics = (
  articleId: string,
  hasFetchedReach = false,
): UseQueryResult<ArticleReadStatistics["companies"]> => {
  return useBuildingQuery({
    queryKey: ["article-read-statistics", articleId],
    queryFn: async () => {
      const response = await client.get(
        `/getArticleReadStatistics?articleId=${articleId}`,
      );
      return response.data;
    },
    // Only fetch read statistics when reach statistics have finished fetching to avoid double-fetching
    // NOTE: only needed if reach and read stats are fetched on the same page
    enabled: hasFetchedReach,
  });
};

export const editArticle = async (
  articleId: string,
  updateData: Partial<Article>,
) => {
  return await client.patch("/editArticle", {
    articleId,
    updateData,
  });
};

export const updateArticleReadLog = async (articleId: string) => {
  return await client.post(`/updateArticleReadLog?articleId=${articleId}`);
};

export const archiveArticle = async (articleId: string) => {
  return await client.post(`/archiveArticle?articleId=${articleId}`);
};

export const cancelPlannedArticlePublication = async (articleId: string) => {
  return await client.post(
    `/cancelPlannedArticlePublication?articleId=${articleId}`,
  );
};

export const publishArticle = async (articleId: string) => {
  return await client.patch(`/publishArticle?articleId=${articleId}`);
};

export const deleteArticle = async (articleId: string) => {
  return await client.delete(`/deleteArticle?articleId=${articleId}`);
};

export const generateArticleContent = async (prompt: string) => {
  return await client.post("/generateArticleContent", {
    prompt,
  });
};

export const generateArticleImage = async (
  title: string,
): Promise<ExportImageData[] | null> => {
  try {
    const response = await client.post(`/generateArticleImage`, {
      title,
    });
    return response.data.images;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const pinArticle = async (articleId: string) => {
  return await client.post(`/updatePinnedArticle?articleId=${articleId}`);
};

export const unpinArticle = async () => {
  return await client.post(`/updatePinnedArticle`);
};
